import { graphql } from 'react-relay';
import React from 'react';

import { Box } from '@atlaskit/primitives/compiled/box';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { fg } from '@confluence/feature-gating';
import { ProfileAvatar } from '@confluence/profile/entry-points/ProfileAvatarComponent.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import type {
	ObjectAddCommentProfileWrapperUserQuery as ObjectAddCommentProfileWrapperUserQuery$data,
	ObjectAddCommentProfileWrapperUserQueryVariables as ObjectAddCommentProfileWrapperUserQuery$variables,
} from './__types__/ObjectAddCommentProfileWrapperUserQuery.ts';
const ObjectAddCommentProfileWrapperUserQuery = graphql`
	query ObjectAddCommentProfileWrapperUserQuery($id: ID!) {
		user: confluence_atlassianUser(id: $id) {
			id
			displayName
			photos {
				value
			}
		}
	}
`;

const styles = cssMap({
	addCommentWrapperStyles: {
		width: '100%',
	},
	addCommentWrapperWithBannerStyles: {
		display: 'grid',
		gridTemplateColumns: '32px minmax(0, 1fr)',
		columnGap: token('space.200'),
		width: '100%',
	},
	addCommentOpenContainerStyles: {
		display: 'flex',
		flexDirection: 'row',
		width: '100%',
		gap: token('space.200'),
		alignItems: 'flex-start',
	},
	addCommentOpenContainerWithBannerStyles: {
		display: 'contents',
	},
	addCommentEditorStyles: {
		gridColumn: '2',
		gridRow: '2',
		width: '100%',
		minWidth: 0,
	},
	bannerStyles: {
		gridColumn: '2',
		gridRow: '1',
		minWidth: 0,
	},
	avatarStyles: {
		gridColumn: '1',
		gridRow: '2',
		width: '32px',
		height: '32px',
		flexShrink: 0,
	},
});

const avatarFloatStyle = { float: 'left' } as const;

export interface ObjectAddCommentProfileWrapperProps {
	children: React.ReactNode;
	banner?: React.ReactNode;
}

export const ObjectAddCommentProfileWrapper = ({
	children,
	banner,
}: ObjectAddCommentProfileWrapperProps): React.JSX.Element => {
	const { userId } = useSessionData();
	const renderServerPlaceholder = useRenderServerPlaceholder();

	const { data: userData, loading } = useQueryInterop<
		ObjectAddCommentProfileWrapperUserQuery$data,
		ObjectAddCommentProfileWrapperUserQuery$variables
	>(ObjectAddCommentProfileWrapperUserQuery, {
		variables: { id: userId || '' },
		fetchPolicy: fg('confluence-comments-fix-avatar-flash') ? 'cache-first' : 'cache-and-network',
		// Skip the query if data is provided or the user is anonymous
		skip: !userId,
	});
	const canViewProfile = true;
	const userInfo = {
		avatarUrl: userData?.user?.photos?.[0]?.value || '',
		accountId: userId,
		displayName: userData?.user?.displayName || '',
	};

	return (
		<Box xcss={banner ? styles.addCommentWrapperWithBannerStyles : styles.addCommentWrapperStyles}>
			{banner ? <Box xcss={styles.bannerStyles}>{banner}</Box> : null}
			<Box
				xcss={
					banner
						? styles.addCommentOpenContainerWithBannerStyles
						: styles.addCommentOpenContainerStyles
				}
			>
				<Box
					xcss={styles.avatarStyles}
					// float left not possible in map
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					style={avatarFloatStyle}
				>
					{loading || renderServerPlaceholder ? null : (
						<ProfileAvatar
							canViewProfile={canViewProfile}
							src={userInfo.avatarUrl}
							userId={userInfo.accountId}
							isAnonymous={!userInfo.accountId}
							name={userInfo.displayName}
							testId="profile-avatar-comment"
						/>
					)}
				</Box>
				<Box xcss={styles.addCommentEditorStyles}>{children}</Box>
			</Box>
		</Box>
	);
};
